import {addMinutes} from 'date-fns/addMinutes';
import {subDays} from 'date-fns/subDays';

import {formatDate} from '@edna/utils';

import {EChannelType, TChannelType} from 'src/constants';
import {combineDateAndTime} from 'src/utils';
import {joi, mixed} from 'src/utils/validator';

import {
  ESmsTrafficType,
  ETariffDetailStatus,
  ETariffDetailType,
  TDetailFormData,
  TTariffChannelType,
  TTariffDetail,
  TTariffDetailsGroupedSearchInfo,
  TTariffPricingDetail,
} from '../definitions';

const moscowTimezone = 'Europe/Moscow';

export const IS_MOSCOW_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone === moscowTimezone;

const convertToMoscowDate = (date: Date = new Date()) => {
  if (IS_MOSCOW_TIMEZONE) {
    return date;
  }

  const localeDate = date
    .toLocaleString('en-GB', {timeZone: moscowTimezone})
    .replace(/^(\d{2})\/(\d{2})\/(\d{4})(.+)$/, '$3/$2/$1$4');

  return new Date(localeDate);
};

export const MIN_DATE = subDays(convertToMoscowDate(), 1);

const getPricingDetailsTemplate = (
  type: TTariffPricingDetail['type'],
): TTariffPricingDetail['data'] => {
  if (type === 'SMS-OPERATOR') {
    return {
      recurrentAmount: 0,
      steps: [
        {
          order: 0,
          lowerLimit: 0,
          upperLimit: 0,
          trafficTypes: [
            {
              label: ESmsTrafficType.AUTH,
              price: 0,
            },
            {
              label: ESmsTrafficType.INFO,
              price: 0,
            },
            {
              label: ESmsTrafficType.SERVICE,
              price: 0,
            },
            {
              label: ESmsTrafficType.MARKETING,
              price: 0,
            },
            {
              label: ESmsTrafficType.ADV,
              price: 0,
            },
          ],
        },
      ],
    };
  }

  return {price: 0};
};

const getDetailsTemplate = (type: TTariffPricingDetail['type']): TTariffDetail['data'] => {
  if (!type.includes('LICENSE')) {
    return;
  }

  if (type === 'WHATSAPP-LICENSE') {
    return {
      mauCount: 0,
      periodMonths: 0,
    };
  }

  return {periodMonths: 0};
};

export const getDefaultDetailValues = (
  channelType: TTariffChannelType,
  detailType: TTariffDetail['type'],
) => {
  const type = `${channelType}-${detailType}`.replaceAll('_', '-') as TTariffPricingDetail['type'];

  return {detail: getDetailsTemplate(type), pricingDetail: getPricingDetailsTemplate(type)};
};

export const getInitialDetailValues = (
  values?: TTariffDetail | TTariffPricingDetail,
  defaultData?: TAnyObject,
): TDetailFormData => {
  const activeFromDate = addMinutes(convertToMoscowDate(), 5);
  const activeFromTime = formatDate(activeFromDate, {format: 'HH:mm'});

  if (!values) {
    return {
      data: defaultData ? JSON.stringify(defaultData, null, 4) : '',
      activeFromTime,
      activeFromDate,
    };
  }

  return {
    ...values,
    data: JSON.stringify(values.data, null, 4),
    activeFromDate,
    activeFromTime,
    activeTo: null,
  };
};

export const getAvailableDetailTypes = (channelType?: TChannelType) => {
  const types = [ETariffDetailType.LICENSE];

  if (channelType === EChannelType.SMS) {
    types.push(ETariffDetailType.OPERATOR);
  }

  if ([EChannelType.WHATSAPP, EChannelType.VIBER].includes(channelType as EChannelType)) {
    types.push(ETariffDetailType.REGION);
  }

  return types;
};

export const filterDetailsByStatus = <
  T extends TTariffDetail | TTariffPricingDetail | TTariffDetailsGroupedSearchInfo,
>(
  values: T[] = [],
  selectedStatus: ETariffDetailStatus = ETariffDetailStatus.ACTIVE,
): T[] =>
  values.filter((value) => {
    if (selectedStatus === ETariffDetailStatus.ACTIVE) {
      return value.active;
    }

    if (selectedStatus === ETariffDetailStatus.PLANNED) {
      return value.planned;
    }

    return;
  });

export const activeFromValidation = {
  activeFromDate: joi
    .date()
    .required()
    .custom((value, helpers) => {
      const [values] = helpers.state.ancestors;
      const time = values.activeFromTime;
      const fullDate = combineDateAndTime(value, time);

      if (fullDate.getTime() <= convertToMoscowDate().getTime()) {
        return helpers.error('expiredTime');
      }

      return value;
    }),
  activeFromTime: joi
    .string()
    .required()
    .custom((value, helpers) => {
      const [values] = helpers.state.ancestors;
      const date = values.activeFromDate;

      if (!date) {
        return value;
      }

      const fullDate = combineDateAndTime(date, value);

      if (fullDate.getTime() <= convertToMoscowDate().getTime()) {
        return helpers.error('expiredTime');
      }

      return value;
    }),
};

export const detailValidation = {
  ...activeFromValidation,
  label: joi.string().required(),
  data: mixed.json,
};
